import React, { useEffect, useMemo, useState } from "react";

import { mdiDeleteOutline, mdiUploadCircleOutline } from "@mdi/js";
import Icon from "@mdi/react";
import classNames from "classnames";
import { useDropzone } from "react-dropzone";
import { useTranslation } from "react-i18next";

import UploadZoneProgress from "./UploadZoneProgress";

import type { JSX } from "react";
import type { Accept } from "react-dropzone";

interface MultipleFileDropzoneProps extends BaseProps<File[] | null> {
  isMultiple: true;
}

interface SingleFileDropzoneProps extends BaseProps<File | null> {
  isMultiple?: false;
}

interface BaseProps<T> {
  accept?: Accept;
  acceptText?: string;
  isInvalid?: boolean;
  uploadProgress?: number;
  isUploadSuccess: boolean;
  isUploadError: boolean;
  withoutPreview?: boolean;
  clearableText?: string;
  initialPreviewUrl?: string;
  onChange: (fileList: T) => void;
  isClearable?: boolean;
  onClear?: () => void;
}

export const PROGRESS_DISPLAY_TIMEOUT_DURATION = 5000;

export default function Dropzone({
  isMultiple,
  onChange,
  onClear,
  isInvalid,
  accept = {
    "image/*": [],
  },
  acceptText,
  uploadProgress,
  isUploadSuccess,
  isUploadError,
  withoutPreview,
  isClearable,
  clearableText,
  initialPreviewUrl,
}: MultipleFileDropzoneProps | SingleFileDropzoneProps): JSX.Element {
  const { t } = useTranslation("forms");
  const [previewUrl, setPreviewUrl] = useState<string | undefined>(undefined);

  const handleChange = (fileList: File[]): void => {
    if (isMultiple) {
      onChange(fileList);
    } else if (fileList[0]) {
      setPreviewUrl(URL.createObjectURL(fileList[0]));
      onChange(fileList[0]);
    }
  };

  const [shouldDisplayProgress, setShouldDisplayProgress] = useState(false);

  const { getRootProps, getInputProps, isFocused, isDragAccept, isDragReject } =
    useDropzone({
      accept,
      multiple: isMultiple,
      onDrop: handleChange,
    });

  useEffect(() => {
    if (uploadProgress && uploadProgress > 0) {
      setShouldDisplayProgress(true);
    }

    if (isUploadSuccess || isUploadError) {
      setTimeout(() => {
        setShouldDisplayProgress(false);
      }, PROGRESS_DISPLAY_TIMEOUT_DURATION);
    }

    if (!previewUrl && initialPreviewUrl) {
      setPreviewUrl(initialPreviewUrl);
    }
  }, [
    uploadProgress,
    isUploadSuccess,
    isUploadError,
    previewUrl,
    initialPreviewUrl,
  ]);

  const handleClear = (): void => {
    onClear?.();
    setPreviewUrl(undefined);
  };

  const rootClasses = classNames(
    "min-w-[150px] text-center flex flex-1 w-full cursor-pointer flex-col items-center justify-center rounded-[0.3125rem] border border-input",
    {
      "ring-2 ring-primary": isFocused,
      "ring-2 ring-status-active": isDragAccept,
      "ring-2 ring-destructive": isDragReject || isInvalid,
      "ring-0 pointer-events-none": shouldDisplayProgress,
      "bg-muted": shouldDisplayProgress && !isUploadSuccess && !isUploadError,
      "aspect-video": !withoutPreview,
      "h-[120px]": withoutPreview,
    },
  );

  const dropzoneClasses = classNames(
    "flex size-full flex-col items-center justify-center bg-card/85 text-foreground transition-opacity rounded-[0.3125rem] overflow-hidden",
    {
      "opacity-0 hover:opacity-100": !withoutPreview && previewUrl,
    },
  );

  const wrapperClasses = classNames(
    "relative flex-1 flex flex-col gap-[0.3125rem]",
  );

  const dynamicRootStyles = useMemo(
    () =>
      withoutPreview || !previewUrl
        ? {}
        : {
            background: `center / contain no-repeat url(${previewUrl}) black`,
          },
    [withoutPreview, previewUrl],
  );

  return (
    <div className={wrapperClasses}>
      <div
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...getRootProps({ className: rootClasses })}
        style={dynamicRootStyles}
      >
        {uploadProgress && shouldDisplayProgress ? (
          <UploadZoneProgress
            isUploadError={isUploadError}
            isUploadSuccess={isUploadSuccess}
            uploadProgress={uploadProgress}
          />
        ) : (
          <div className={dropzoneClasses}>
            <Icon path={mdiUploadCircleOutline} size={1.25} />
            <p className="mt-[0.3125rem]">
              <span className="text-primary font-semibold">
                {t("clickHereToUpload")}
              </span>
              &nbsp;
              {t("or", { ns: "editor-content" })}
              &nbsp;
              {t("dragAndDrop")}
            </p>
            <p>
              {acceptText ??
                `SVG, PNG, JPG ${t("or", { ns: "editor-content" })} GIF`}
            </p>
            <input
              // eslint-disable-next-line react/jsx-props-no-spreading
              {...getInputProps()}
            />
          </div>
        )}
      </div>

      {isClearable && previewUrl ? (
        <div
          className="text-destructive flex w-full cursor-pointer items-center justify-end text-[0.78125rem]"
          onClick={handleClear}
        >
          <Icon path={mdiDeleteOutline} size={0.625} />
          {clearableText ?? t("delete")}
        </div>
      ) : null}
    </div>
  );
}
